import {
  CircularProgress,
  Flex,
  ListItem,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import { useQueries } from "@tanstack/react-query";
import { GenericError } from "src/components/Feedback/GenericError";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import * as RD from "src/types/remoteData";
import { type ConditionsDescription } from "./conditionsForm";
import { type InheritedCriterionSource } from "./criterionForEntity";
import { type LoadedCriterion } from "./fetchCriterion";

export function EntityConditionsDescription({
  criterion,
  hasInheritedSources,
  useSeatTypes,
}: {
  criterion: LoadedCriterion;
  hasInheritedSources: boolean;
  useSeatTypes: boolean;
}) {
  const label = hasInheritedSources
    ? `Conditions specific to this ${useSeatTypes ? "seat type" : "offering"}`
    : undefined;
  return (
    <ConditionsDescriptionBlock
      label={label}
      conditionsDescription={criterion.description}
      showEmptyMessage={true}
    />
  );
}

export function InheritedConditionsDescriptions({
  sources,
  criteriaSetId,
  enabled = true,
}: {
  sources: readonly InheritedCriterionSource[];
  criteriaSetId: string;
  enabled?: boolean;
}) {
  const results = useQueries({
    queries: sources.map((source) => ({
      queryKey: source.queryKey(criteriaSetId),
      enabled,
      queryFn: () => source.fetch(criteriaSetId),
    })),
  });
  const loaded = RD.combine(
    sources.map((source, index) => {
      const result = results[index];
      return RD.fromRestResult<Error, LoadedCriterion>({
        data: result?.data,
        error: result?.error,
        isError: result?.isError ?? false,
        isLoading: result?.isLoading ?? true,
        isRefetching: result?.isRefetching ?? false,
      }).map((criterion) => ({ source, criterion }));
    })
  );

  if (sources.length === 0) return null;

  return (
    <RemoteDataView
      remoteData={loaded}
      loading={<PanelLoading />}
      error={(error) => <GenericError error={error} />}
    >
      {(inheritedCriteria) => {
        const inheritedCriterionDescriptions = inheritedCriteria.filter(
          (item) => !isEmptyDescription(item.criterion.description)
        );
        if (inheritedCriterionDescriptions.length === 0) {
          return (
            <Text fontSize="sm" fontStyle="italic" color="gray.600">
              No inherited conditions configured
            </Text>
          );
        }
        return (
          <VStack align="stretch" spacing={4}>
            {inheritedCriterionDescriptions.map((item) => (
              <ConditionsDescriptionBlock
                key={item.source.id}
                label={`Inherited from ${item.source.label}`}
                conditionsDescription={item.criterion.description}
                showEmptyMessage={false}
              />
            ))}
          </VStack>
        );
      }}
    </RemoteDataView>
  );
}

function ConditionsDescriptionBlock({
  label,
  conditionsDescription,
  showEmptyMessage,
}: {
  label?: string;
  conditionsDescription: ConditionsDescription;
  showEmptyMessage: boolean;
}) {
  if (isEmptyDescription(conditionsDescription) && !showEmptyMessage)
    return null;
  return (
    <VStack align="stretch" spacing={2}>
      {label && (
        <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
          {label}
        </Text>
      )}
      {isEmptyDescription(conditionsDescription) ? (
        <Text fontSize="sm" fontStyle="italic" color="gray.600">
          No set conditions
        </Text>
      ) : conditionsDescription.lines.length === 1 ? (
        <Text fontSize="sm">{conditionsDescription.lines[0]}</Text>
      ) : (
        <>
          <Text fontSize="sm">
            {conditionsDescription.matchMode === "all" ? "All of:" : "Any of:"}
          </Text>
          <UnorderedList fontSize="sm" pl={1}>
            {conditionsDescription.lines.map((line, index) => (
              <ListItem key={`${line}-${index}`}>{line}</ListItem>
            ))}
          </UnorderedList>
        </>
      )}
    </VStack>
  );
}

function isEmptyDescription(
  description: ConditionsDescription
): description is Extract<ConditionsDescription, { type: "none" }> {
  return description.type !== "list" || description.lines.length === 0;
}

export function PanelLoading() {
  return (
    <Flex justify="center" py={8}>
      <CircularProgress isIndeterminate color="primary.500" />
    </Flex>
  );
}
